import type { CatalogProduct } from '../../types'
import type { FacetFieldSource } from './types'

const BRAND_FIELD_KEYS = new Set(['brand', 'marca', 'manufacturer', 'brandname', 'vendor', 'produttore'])
const NUMERIC_FACET_KEYS = new Set([
  'capacity',
  'coolingcapacity',
  'heatingcapacity',
  'noiselevel',
  'power',
  'screensize',
  'size',
  'voltage',
])
const CHECKBOX_FACET_KEYS = new Set(['refrigerant', 'refrigerantgas', 'gasrefrigerante'])
const EXCLUDED_FACET_KEYS = new Set([
  'warranty',
  'warrantyyear',
  'warrantyyears',
  'warrantyperiod',
  'garanzia',
  'garanziaanno',
  'garanziaanni',
])

const normalizeFieldKeyForMatch = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, '')

export const isDiscreteFacetKey = (key: string) => normalizeFieldKeyForMatch(key) === 'btu'
export const isNumericFacetKey = (key: string) => NUMERIC_FACET_KEYS.has(normalizeFieldKeyForMatch(key))
export const isCheckboxFacetKey = (key: string) => CHECKBOX_FACET_KEYS.has(normalizeFieldKeyForMatch(key))

const isBrandFieldKey = (key: string) => BRAND_FIELD_KEYS.has(normalizeFieldKeyForMatch(key))

export const sanitizeOptionValue = (value: unknown): string | null => {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

export const toLooseNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const candidate = value.trim().replace(',', '.').match(/-?\d+(\.\d+)?/)
  if (!candidate) return null
  const parsed = Number(candidate[0])
  return Number.isFinite(parsed) ? parsed : null
}

export const normalizeFieldLabel = (key: string) =>
  ({
    btu: 'BTU',
    capacity: 'Capacità',
    classeenergetica: 'Classe energetica',
    coolingenergyclass: 'Efficienza di raffreddamento',
    coolingenergy: 'Efficienza di raffreddamento',
    color: 'Colore',
    colore: 'Colore',
    coolingcapacity: 'Capacità di raffreddamento',
    heatingenergyclass: 'Efficienza di riscaldamento',
    heatingenergy: 'Efficienza di riscaldamento',
    installation: 'Installazione',
    installationtype: 'Installazione',
    material: 'Materiale',
    noiselevel: 'Livello di rumore',
    power: 'Potenza',
    refrigerant: 'Gas refrigerante',
    refrigerantgas: 'Gas refrigerante',
    series: 'Serie',
    screensize: 'Dimensione schermo',
    size: 'Dimensione',
    voltage: 'Voltaggio',
    wificontrol: 'Controllo Wi-Fi',
  }[normalizeFieldKeyForMatch(key)] ??
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^./, (value) => value.toUpperCase()))

export const isFilterableFacetKey = (key: string) => {
  const normalized = normalizeFieldKeyForMatch(key)
  return !EXCLUDED_FACET_KEYS.has(normalized) && !BRAND_FIELD_KEYS.has(normalized)
}

export const formatFacetLabelFromId = (id: string, language: 'it' | 'en') => {
  const key = id.replace(/^.*:/, '')
  if (language === 'en') return key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().replace(/^./, (value) => value.toUpperCase())
  return normalizeFieldLabel(key)
}

const PRODUCT_TYPE_LABELS: Record<string, { it: string; en: string }> = {
  'air-conditioner': { it: 'Climatizzatore', en: 'Air conditioner' },
  appliance: { it: 'Elettrodomestico', en: 'Appliance' },
  generic: { it: 'Prodotto', en: 'Product' },
  hvac: { it: 'Climatizzazione', en: 'HVAC' },
}

export const formatProductTypeLabel = (value: string, language: 'it' | 'en') => {
  const normalized = value.trim().toLowerCase()
  return PRODUCT_TYPE_LABELS[normalized]?.[language] ?? value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (entry) => entry.toUpperCase())
}

const flattenFieldValue = (value: unknown): unknown[] => {
  if (value === null || value === undefined) return []
  if (Array.isArray(value)) return value
  return [value]
}

export const extractFieldValues = (product: CatalogProduct, source: FacetFieldSource, key: string): unknown[] => {
  if (source === 'custom') {
    return flattenFieldValue(product.customFields?.[key])
  }
  if (!Array.isArray(product.metadata)) return []
  return product.metadata
    .filter((entry) => entry.key === key)
    .map((entry) => entry.value)
    .filter((value) => typeof value === 'string' && value.trim().length > 0)
}

export const computeProductPrice = (product: CatalogProduct) => {
  const value = product.price
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export const formatOptionLabel = (value: string, language: 'it' | 'en') => {
  if (value === 'true') return language === 'en' ? 'Yes' : 'Sì'
  if (value === 'false') return language === 'en' ? 'No' : 'No'
  return value
}

export const formatFacetOptionLabel = (value: string, key: string, language: 'it' | 'en') => {
  if (isDiscreteFacetKey(key)) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return `${new Intl.NumberFormat(language === 'en' ? 'en-GB' : 'it-IT').format(parsed)} BTU`
    }
  }
  return formatOptionLabel(value, language)
}

export const extractProductBrands = (product: CatalogProduct): string[] => {
  const values = new Set<string>()
  Object.entries(product.customFields ?? {}).forEach(([key, value]) => {
    if (!isBrandFieldKey(key)) return
    flattenFieldValue(value).forEach((entry) => {
      const normalized = sanitizeOptionValue(entry)
      if (normalized) values.add(normalized)
    })
  })
  ;(product.metadata ?? []).forEach((entry) => {
    if (!isBrandFieldKey(entry.key)) return
    const normalized = sanitizeOptionValue(entry.value)
    if (normalized) values.add(normalized)
  })
  return Array.from(values)
}
